import { useEffect, useMemo, useState } from 'react';
import { ArrowLeft, Mail, Phone, ShieldCheck } from 'lucide-react';
import { BrandLogo } from './BrandLogo';
import { LegalFooter } from './LegalFooter';
import { type LegalPage as LegalPageData, type LegalSection, operatorDetails } from '../data/legalPages';
import type { ShippingRatesPayload } from '../data/shippingRates';

type LegalPageProps = {
  page: LegalPageData;
};

type RuntimeShippingRates = {
  policy?: {
    carrier?: string;
    sourceCheckedAt?: string;
    nextTariffReview?: string;
  };
  outboundCustomerRates?: Array<{
    label: string;
    condition: string;
    customerPrice: number | null;
  }>;
  returnCostRates?: Array<{
    label: string;
    customerCost: number | null;
    responsibility: string;
  }>;
};

function formatShippingPrice(price: number | null): string {
  if (price === null) return 'op offerte';
  if (price === 0) return 'gratis';
  return `EUR ${price.toFixed(2)}`;
}

function withRuntimeShippingRates(sections: LegalSection[], rates: RuntimeShippingRates | null): LegalSection[] {
  if (!rates?.outboundCustomerRates?.length && !rates?.returnCostRates?.length) return sections;

  return sections.map((section) => {
    if (section.heading === 'Standaard verzendtarieven voor verkoopstart' && rates.outboundCustomerRates?.length) {
      return {
        ...section,
        body: [
          `${rates.policy?.carrier ?? 'PostNL'} is de basis voor de eerste NL/BE verzendtabel. De tabel is gecontroleerd op ${rates.policy?.sourceCheckedAt ?? 'onbekend'} en wordt opnieuw bekeken rond ${rates.policy?.nextTariffReview ?? 'de volgende PostNL aankondiging'}.`,
          'De klantaanpak is eenvoudig: vaste verzendbijdrage onder de drempel, gratis verzending boven de drempel en maatwerk voor grote pakketten of clubpakketten.'
        ],
        bullets: rates.outboundCustomerRates.map((rate) => `${rate.label}: ${formatShippingPrice(rate.customerPrice)} (${rate.condition}).`)
      };
    }

    if (section.heading === 'Standaard retourkosten voor verkoopstart' && rates.returnCostRates?.length) {
      return {
        ...section,
        bullets: rates.returnCostRates.map((rate) => {
          const price = rate.customerCost === null ? 'op offerte' : `indicatief EUR ${rate.customerCost.toFixed(2)}`;
          return `${rate.label}: ${price}. ${rate.responsibility}`;
        })
      };
    }

    return section;
  });
}

export function LegalPage({ page }: LegalPageProps) {
  const [runtimeShippingRates, setRuntimeShippingRates] = useState<ShippingRatesPayload | RuntimeShippingRates | null>(null);

  useEffect(() => {
    document.title = `${page.title} | ProPickleball.nl`;
    const description = document.querySelector<HTMLMetaElement>('meta[name="description"]');
    if (description) description.content = page.summary;
  }, [page]);

  useEffect(() => {
    if (!['/levering-en-verzending/', '/retouren/'].includes(page.slug)) return;

    const controller = new AbortController();
    fetch('/data/shipping-rates.json', { signal: controller.signal, cache: 'no-store' })
      .then((response) => (response.ok ? response.json() : null))
      .then((payload) => {
        if (payload) setRuntimeShippingRates(payload);
      })
      .catch(() => {
        if (!controller.signal.aborted) setRuntimeShippingRates(null);
      });

    return () => controller.abort();
  }, [page.slug]);

  const sections = useMemo(
    () => withRuntimeShippingRates(page.sections, runtimeShippingRates),
    [page.sections, runtimeShippingRates]
  );

  return (
    <>
      <main className="legalPage">
        <nav className="legalTopbar" aria-label="Legal navigatie">
          <BrandLogo compact />
          <a className="backHome" href="/"><ArrowLeft size={18} aria-hidden="true" /> Terug naar de webshop</a>
        </nav>

        <header className="legalHero">
          <p className="eyebrow">Service & juridisch</p>
          <h1>{page.title}</h1>
          <p>{page.summary}</p>
          <div className="legalMeta" aria-label="Pagina status">
            <span><ShieldCheck size={18} aria-hidden="true" /> Informatiepagina</span>
            <span>Laatst bijgewerkt: {page.updated}</span>
          </div>
        </header>

        <div className="legalLayout">
          <aside className="legalAside" aria-label="Bedrijfsgegevens">
            <h2>Ondernemer</h2>
            <p><strong>{operatorDetails.legalOwner}</strong><br />{operatorDetails.operatingGroup}</p>
            <p>{operatorDetails.address}</p>
            <p>KVK {operatorDetails.kvk}<br />BTW {operatorDetails.vat}</p>
            <a href={`mailto:${operatorDetails.email}`}><Mail size={17} aria-hidden="true" /> {operatorDetails.email}</a>
            <a href="tel:+31619200380"><Phone size={17} aria-hidden="true" /> {operatorDetails.phone}</a>
          </aside>

          <article className="legalArticle">
            {sections.map((section) => (
              <section key={section.heading}>
                <h2>{section.heading}</h2>
                {section.body?.map((paragraph) => <p key={paragraph}>{paragraph}</p>)}
                {section.bullets ? (
                  <ul>
                    {section.bullets.map((bullet) => <li key={bullet}>{bullet}</li>)}
                  </ul>
                ) : null}
              </section>
            ))}
          </article>
        </div>
      </main>
      <LegalFooter />
    </>
  );
}
